import * as React from 'react';

type CardVariant = 'default' | 'glass' | 'gradient' | 'elevated' | 'outlined';
type CardPadding = 'none' | 'sm' | 'md' | 'lg';

interface ModernCardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  padding?: CardPadding;
  hover?: boolean;
  animate?: boolean;
  darkMode?: boolean;
}

const paddingClasses: Record<CardPadding, string> = {
  none: '',
  sm: 'p-3',
  md: 'p-5',
  lg: 'p-6',
};

export const ModernCard = React.forwardRef<HTMLDivElement, ModernCardProps>(
  (
    {
      className = '',
      variant = 'default',
      padding = 'md',
      hover = false,
      animate = false,
      darkMode = false,
      children,
      ...props
    },
    ref
  ) => {
    const baseClasses = 'relative overflow-hidden rounded-2xl transition-all duration-300';

    const variantClasses: Record<CardVariant, string> = {
      default: darkMode
        ? 'bg-slate-800/80 border border-slate-700/50'
        : 'bg-white border border-gray-200 shadow-sm',
      glass: darkMode
        ? 'bg-slate-800/60 backdrop-blur-xl border border-slate-700/50'
        : 'bg-white/70 backdrop-blur-xl border border-white/20 shadow-xl',
      gradient: darkMode
        ? 'bg-gradient-to-br from-slate-800/90 to-slate-900/90 border border-slate-700/50'
        : 'bg-gradient-to-br from-white to-gray-50 border border-gray-100 shadow-lg',
      elevated: darkMode
        ? 'bg-slate-800 border border-slate-700/50 shadow-2xl shadow-black/20'
        : 'bg-white border border-gray-100 shadow-xl shadow-gray-200/50',
      outlined: darkMode
        ? 'bg-transparent border-2 border-slate-700'
        : 'bg-transparent border-2 border-gray-200',
    };

    const hoverClasses = hover
      ? 'hover:scale-[1.02] hover:shadow-lg cursor-pointer card-hover'
      : '';

    const animateClasses = animate ? 'animate-fade-in' : '';

    return (
      <div
        ref={ref}
        className={`${baseClasses} ${variantClasses[variant]} ${paddingClasses[padding]} ${hoverClasses} ${animateClasses} ${className}`}
        style={animate ? { opacity: 0 } : undefined}
        {...props}
      >
        {children}
      </div>
    );
  }
);
ModernCard.displayName = 'ModernCard';

// Card Header with icon and title
interface CardHeaderModernProps extends React.HTMLAttributes<HTMLDivElement> {
  icon?: React.ReactNode;
  iconBg?: string;
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  darkMode?: boolean;
}

export const CardHeaderModern = React.forwardRef<HTMLDivElement, CardHeaderModernProps>(
  ({ icon, iconBg = 'bg-gradient-to-br from-blue-500 to-indigo-600', title, subtitle, action, darkMode = false, className = '', ...props }, ref) => {
    return (
      <div ref={ref} className={`flex items-center justify-between mb-4 ${className}`} {...props}>
        <div className="flex items-center gap-3">
          {icon && (
            <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${iconBg} shadow-lg`}>
              {icon}
            </div>
          )}
          <div>
            <h3 className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {title}
            </h3>
            {subtitle && (
              <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                {subtitle}
              </p>
            )}
          </div>
        </div>
        {action && <div>{action}</div>}
      </div>
    );
  }
);
CardHeaderModern.displayName = 'CardHeaderModern';

// Stat Card Component
interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  iconBg?: string;
  trend?: { value: number; isPositive: boolean };
  darkMode?: boolean;
  className?: string;
}

export function StatCard({ title, value, icon, iconBg = 'bg-gradient-to-br from-blue-500 to-indigo-600', trend, darkMode = false, className = '' }: StatCardProps) {
  return (
    <ModernCard variant="gradient" darkMode={darkMode} hover className={className}>
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <p className={`text-sm font-medium ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
            {title}
          </p>
          <p className={`text-3xl font-bold tracking-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {value}
          </p>
          {trend && (
            <div className="flex items-center gap-1">
              <span className={`text-sm font-medium ${trend.isPositive ? 'text-emerald-500' : 'text-rose-500'}`}>
                {trend.isPositive ? '↑' : '↓'} {trend.value}%
              </span>
            </div>
          )}
        </div>
        <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${iconBg} shadow-lg`}>
          {icon}
        </div>
      </div>
    </ModernCard>
  );
}

// Glass Info Card
interface GlassInfoCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  darkMode?: boolean;
}

export function GlassInfoCard({ icon, label, value, darkMode = false }: GlassInfoCardProps) {
  return (
    <div className={`group rounded-2xl p-3 text-center transition-all duration-300 hover:scale-105 cursor-default ${
      darkMode
        ? 'bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20'
        : 'bg-white/80 backdrop-blur-md border border-white/50 shadow-lg hover:shadow-xl'
    }`}>
      <div className="w-10 h-10 mx-auto mb-2 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg group-hover:shadow-blue-500/30">
        {icon}
      </div>
      <p className={`text-[10px] uppercase tracking-wider ${darkMode ? 'text-white/60' : 'text-gray-500'}`}>
        {label}
      </p>
      <p className={`font-bold text-sm mt-0.5 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        {value}
      </p>
    </div>
  );
}

export default ModernCard;
